import { TimeStampMs } from '@fullstory/recording-api';
import { sortedLastIndexBy } from 'lodash';

type Entry<Value> = {
  value: Value;
  timeMs: TimeStampMs;
};

/**
 * An iterable sequence of values representing the value of a
 * {@link CumulativeTimeline} at a particular point in time.
 *
 * Mutating a {@link CumulativeTimeline} while iterating over a
 * {@link CumulativeSequence} derived from it may result in values getting
 * skipped.
 */
export class CumulativeSequence<Value> {
  private _cachedSequenceLength: number | undefined = undefined;
  private _cachedTimelineLength: number;

  constructor(
    private readonly _entries: readonly Entry<Value>[],
    private readonly _upperBoundMs: TimeStampMs,
  ) {
    this._cachedTimelineLength = this._entries.length;
  }

  /**
   * @returns the length of the sequence. O(log N) if length() hasn't been
   * called yet, or if the underlying timeline has been mutated since the last
   * call; otherwise, O(1).
   */
  get length(): number {
    // Recalculate the length if we haven't calculated it before, or if the
    // underlying timeline has been mutated since the last time we calculated
    // the length. Values can be added to a CumulativeTimeline, but they can't
    // be removed or changed, so we can tell if the timeline has been mutated
    // just by checking the number of entries it has.
    if (
      this._cachedSequenceLength === undefined ||
      this._cachedTimelineLength !== this._entries.length
    ) {
      this._cachedSequenceLength = sortedLastIndexBy(
        this._entries,
        { value: undefined as unknown as Value, timeMs: this._upperBoundMs },
        e => e.timeMs,
      );
      this._cachedTimelineLength = this._entries.length;
    }

    return this._cachedSequenceLength;
  }

  *[Symbol.iterator](): IterableIterator<Value> {
    for (const entry of this._entries) {
      if (entry.timeMs > this._upperBoundMs) {
        break;
      }
      yield entry.value;
    }
  }
}

/**
 * An immutable view of a {@link CumulativeTimeline}.
 */
export interface ImmutableCumulativeTimeline<Value> {
  /** @returns all values on the timeline, up to and including 'time'. */
  at(time: TimeStampMs): CumulativeSequence<Value>;
}

/**
 * A timeline where values are cumulative - in other words, the value of the
 * timeline at each point is the union of the values of the timeline at every
 * previous point.
 *
 * This class is quite a bit simpler than {@link Timeline} because the features
 * that make {@link Timeline} complex, like duration and priority, don't make
 * sense in a cumulative timeline. Every value effectively has infinite
 * duration, and priority isn't meaningful since values are never overwritten.
 */
export class CumulativeTimeline<Value> implements ImmutableCumulativeTimeline<Value> {
  private readonly _events: Entry<Value>[] = [];

  add(value: Value, timeMs: TimeStampMs): void {
    const entry = { value, timeMs };
    const index = sortedLastIndexBy(this._events, entry, e => e.timeMs);
    this._events.splice(index, 0, entry);
  }

  at(timeMs: TimeStampMs): CumulativeSequence<Value> {
    return new CumulativeSequence<Value>(this._events, timeMs);
  }
}
